// ── New Task modal "Depends on" chip typeahead (TPT598) ──
//
// Candidates are queried from the server as the user types — one bounded summary page of
// GET /tasks per query (lib/parent-picker.js taskSearchPath(), the contract the drawer's Change
// Parent picker uses) — never read off a caller's task rows. A board holds only its filtered
// page (TPT597), so a list-backed picker would silently hide every task outside the filter.
//
// Same dropdown discipline as lib/tag-typeahead.js, and the same `.tag-typeahead*` classes so it
// shares that styling: body-appended `position: fixed` dropdown (a modal's overflow can't clip
// it), mousedown+preventDefault on rows, a guarded blur timer, capture-phase scroll/resize
// repositioning, and an Escape that stopPropagation()s while the dropdown is open so it never
// reaches openModal()'s document-level Escape.

import { api } from '../api.js';
import { escapeHtml, debounce } from '../utils.js';
import { taskSearchPath, dependencyCandidates, PARENT_PICKER_LIMIT } from '../lib/parent-picker.js';

function escapeAttr(s) {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

// attachDependencyTypeahead(input, opts) — replaces `input` (a plain text <input>, already in
// the DOM) with a chip row wrapping it.
//
// opts:
//   projectId      number|string  required — scopes the task search
//   initialKeys    string[]       task keys to seed as chips (default [])
//   scrollEl       Element|null   scroll container to reposition the dropdown against
//   onChange(keys)                fired after a user add/remove
//   isAlive()      boolean        false once the host UI is gone (guards timers and replies)
//
// Returns { getKeys, close, destroy }. Keys are task_key strings, the `dependencies` format.
export function attachDependencyTypeahead(input, opts = {}) {
  const noop = { getKeys: () => [], close() {}, destroy() {} };
  if (!input) return noop;

  const { projectId, initialKeys = [], scrollEl = null, onChange } = opts;
  const isAliveFn = typeof opts.isAlive === 'function' ? opts.isAlive : () => true;

  const wrapper = document.createElement('div');
  wrapper.className = 'tag-typeahead';
  input.replaceWith(wrapper);

  const chipsHost = document.createElement('span');
  chipsHost.className = 'tag-typeahead-chips';
  wrapper.appendChild(chipsHost);
  wrapper.appendChild(input);
  input.classList.add('tag-typeahead-input');
  input.type = 'text';
  input.autocomplete = 'off';
  input.maxLength = 200; // the list route's q cap
  if (!input.placeholder) input.placeholder = 'Search tasks by key or title...';

  let keys = [...new Set((initialKeys || []).filter(k => typeof k === 'string' && k))];
  const titles = new Map(); // task_key -> title, for chip tooltips
  let dropdown = null;
  let rows = [];              // last reply's rows (already excluding chosen keys)
  let full = false;           // last reply filled a whole page
  let message = '';           // loading / error / empty text shown instead of rows
  let activeIndex = -1;
  let blurTimer = null;
  let seq = 0;
  let destroyed = false;

  const alive = () => !destroyed && isAliveFn();

  function fireChange() {
    if (typeof onChange === 'function') onChange(keys.slice());
  }

  function renderChips() {
    chipsHost.innerHTML = keys.map(key => {
      const title = titles.get(key);
      return `<span class="tag tag-typeahead-chip"${title ? ` title="${escapeAttr(title)}"` : ''}>${escapeHtml(key)}<span class="tag-typeahead-chip-remove" data-key="${escapeAttr(key)}">&times;</span></span>`;
    }).join('');
  }

  chipsHost.addEventListener('mousedown', (e) => {
    const btn = e.target.closest('.tag-typeahead-chip-remove');
    if (!btn) return;
    e.preventDefault(); // keep focus on the input
    keys = keys.filter(k => k !== btn.dataset.key);
    renderChips();
    fireChange();
    if (dropdown) search();
  });

  function positionDropdown() {
    if (!dropdown) return;
    const r = wrapper.getBoundingClientRect();
    dropdown.style.top = `${r.bottom + 2}px`;
    dropdown.style.left = `${r.left}px`;
    dropdown.style.width = `${Math.max(r.width, 220)}px`;
  }

  const onScrollOrResize = () => positionDropdown();
  if (scrollEl) scrollEl.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);

  function updateActive() {
    if (!dropdown) return;
    dropdown.querySelectorAll('.tag-typeahead-option').forEach((el, i) => {
      el.classList.toggle('active', i === activeIndex);
    });
  }

  function renderDropdown() {
    if (!dropdown) {
      dropdown = document.createElement('div');
      dropdown.className = 'tag-typeahead-dropdown';
      dropdown.addEventListener('mousedown', onDropdownMousedown);
      document.body.appendChild(dropdown);
    }
    if (activeIndex >= rows.length) activeIndex = rows.length ? 0 : -1;
    dropdown.innerHTML = message
      ? `<div class="tag-typeahead-empty">${escapeHtml(message)}</div>`
      : rows.map((t, i) => `<div class="tag-typeahead-option" data-index="${i}">${escapeHtml(t.task_key)}<small>${escapeHtml(t.title || '')}</small></div>`).join('')
        + (full ? `<div class="tag-typeahead-empty">Showing the first ${PARENT_PICKER_LIMIT} tasks — type to narrow.</div>` : '');
    updateActive();
    positionDropdown();
  }

  function hideDropdown() {
    seq++; // a reply still in flight must not reopen it
    if (dropdown) { dropdown.remove(); dropdown = null; }
    rows = [];
    activeIndex = -1;
  }

  // One server query for the input's current text; a reply superseded by a newer query, or
  // landing after the dropdown closed or the host went away, is dropped.
  async function search() {
    if (!alive()) return;
    // A debounced call that fires after blur already closed the dropdown must not reopen it.
    if (!dropdown && document.activeElement !== input) return;
    const mySeq = ++seq;
    if (!dropdown) { rows = []; message = 'Loading...'; full = false; renderDropdown(); }
    let reply;
    try {
      reply = await api.get(taskSearchPath(projectId, input.value));
    } catch (err) {
      if (mySeq !== seq || !alive() || !dropdown) return;
      rows = [];
      full = false;
      message = (err && err.message) || 'Failed to load tasks';
      renderDropdown();
      return;
    }
    if (mySeq !== seq || !alive() || !dropdown) return;
    const page = (reply && reply.tasks) || [];
    full = page.length >= PARENT_PICKER_LIMIT;
    rows = dependencyCandidates(page, keys);
    message = rows.length || full ? '' : 'No matching tasks';
    activeIndex = rows.length ? 0 : -1;
    renderDropdown();
  }

  const debouncedSearch = debounce(search, 250);

  function commit(row) {
    if (!keys.includes(row.task_key)) {
      keys.push(row.task_key);
      titles.set(row.task_key, row.title || '');
      fireChange();
    }
    input.value = '';
    renderChips();
    search(); // stay open, refreshed without the key just added — continuous multi-add
  }

  function onDropdownMousedown(e) {
    const opt = e.target.closest('.tag-typeahead-option');
    e.preventDefault(); // keep focus on the input — never let a click on the dropdown blur it
    if (!opt) return;
    const row = rows[Number(opt.dataset.index)];
    if (row) commit(row);
  }

  input.addEventListener('focus', () => {
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
    if (!dropdown) search();
  });

  input.addEventListener('click', () => { if (!dropdown) search(); });

  input.addEventListener('input', () => debouncedSearch());

  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!dropdown) { search(); return; }
      if (rows.length) { activeIndex = (activeIndex + 1) % rows.length; updateActive(); }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (dropdown && rows.length) { activeIndex = (activeIndex - 1 + rows.length) % rows.length; updateActive(); }
    } else if (e.key === 'Enter') {
      // Always prevented — this field sits inside the New Task <form>, where a bare Enter submits.
      e.preventDefault();
      if (dropdown && rows.length && activeIndex >= 0) commit(rows[activeIndex]);
    } else if (e.key === 'Backspace' && !input.value && keys.length) {
      keys.pop();
      renderChips();
      fireChange();
      if (dropdown) search();
    } else if (e.key === 'Escape' && dropdown) {
      e.preventDefault();
      e.stopPropagation();
      hideDropdown();
    }
  });

  input.addEventListener('blur', () => {
    blurTimer = setTimeout(() => {
      blurTimer = null;
      if (!alive()) return;
      hideDropdown();
      input.value = '';
    }, 150);
  });

  function getKeys() { return keys.slice(); }

  function destroy() {
    destroyed = true;
    hideDropdown();
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
    if (scrollEl) scrollEl.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);
  }

  renderChips();
  return { getKeys, close: hideDropdown, destroy };
}
